import { Capacitor } from '@capacitor/core';
import { Geolocation, type Position } from '@capacitor/geolocation';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App } from '@capacitor/app';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform();

export async function requestLocationPermission(): Promise<boolean> {
  if (!isNative) return true;
  const status = await Geolocation.checkPermissions();
  if (status.location === 'granted') return true;
  const req = await Geolocation.requestPermissions();
  return req.location === 'granted';
}

export async function getCurrentPosition(): Promise<Position | null> {
  if (!isNative) {
    if (!('geolocation' in navigator)) return null;
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({
          coords: {
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
            accuracy: p.coords.accuracy,
            altitude: p.coords.altitude,
            altitudeAccuracy: p.coords.altitudeAccuracy,
            heading: p.coords.heading,
            speed: p.coords.speed,
          },
          timestamp: p.timestamp,
        } as Position),
        () => resolve(null),
        { enableHighAccuracy: true, maximumAge: 5_000, timeout: 10_000 },
      );
    });
  }
  try {
    return await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10_000 });
  } catch {
    return null;
  }
}

export async function watchPosition(cb: (p: Position) => void): Promise<() => void> {
  if (!isNative) {
    const id = navigator.geolocation.watchPosition(
      (p) => cb({
        coords: {
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracy: p.coords.accuracy,
          altitude: p.coords.altitude,
          altitudeAccuracy: p.coords.altitudeAccuracy,
          heading: p.coords.heading,
          speed: p.coords.speed,
        },
        timestamp: p.timestamp,
      } as Position),
      undefined,
      { enableHighAccuracy: true, maximumAge: 5_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }
  const watchId = await Geolocation.watchPosition(
    { enableHighAccuracy: true, timeout: 10_000 },
    (pos) => { if (pos) cb(pos); },
  );
  return () => Geolocation.clearWatch({ id: watchId });
}

export async function vibrate(style: 'light' | 'medium' | 'heavy' = 'medium') {
  if (!isNative) {
    if ('vibrate' in navigator) navigator.vibrate(style === 'heavy' ? 200 : style === 'medium' ? 100 : 30);
    return;
  }
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  await Haptics.impact({ style: map[style] });
}

export function listenBackButton(onBack: () => boolean | void) {
  if (!isNative) return () => undefined;
  const handle = App.addListener('backButton', () => {
    const handled = onBack();
    if (!handled) App.exitApp();
  });
  return () => { void handle.then((h) => h.remove()); };
}
